import React, { useCallback, useEffect, useState } from "react";
import { confirm as confirmDialog } from "../../util/confirmDialog";
import {
    Box, Button, Chip, CircularProgress, IconButton, Paper,
    Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ActionButton from "../../component/common/ActionButton";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import { notify } from "../../util/toast";
import { runApiAction, serverMsgOr } from "../../util/apiFeedback";
import { calendarApi } from "./utils/calendarApi";
import { EventDialog } from "./component/EventDialog";
import { getEventColor, asList } from "./utils/calendarUtils";

import {
    endOfDayString,
    formatDate,
    formatMonthDayKo,
    formatTime,
    sortByStartAt,
    startOfDayString,
    today as getToday,
    toDayjs,
} from "../../util/dateUtils";

export default function MySchedulePage({ me }) {
    const today = getToday();
    const [dateFrom, setDateFrom] = useState(formatDate(today.startOf("month")));
    const [dateTo, setDateTo] = useState(formatDate(today.endOf("month")));
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogEvent, setDialogEvent] = useState(null);

    const loadMyEvents = useCallback(async () => {
        if (!me?.id) return;
        setLoading(true);
        try {
            const from = startOfDayString(dateFrom);
            const to = endOfDayString(dateTo);
            const res = await calendarApi.getEvents(from, to, [me.id], []);
            const list = asList(res);
            setEvents(list.filter(ev => ev.createdById === me.id));
        } catch {
            notify("일정 불러오기 실패", "error");
        } finally {
            setLoading(false);
        }
    }, [me, dateFrom, dateTo]);

    /* me 로드 완료 시 최초 조회 */
    useEffect(() => {
        if (me?.id) loadMyEvents();
    }, [me]);

    const handleEdit = (ev) => {
        setDialogEvent({ ...ev, _isOwner: true });
        setDialogOpen(true);
    };

    const handleAdd = () => {
        setDialogEvent(null);
        setDialogOpen(true);
    };

    const handleSave = async (id, payload) => {
        if (id) { await calendarApi.update(id, payload); return id; }
        const res = await calendarApi.create(payload);
        return (res?.data ?? res)?.id;
    };

    const handleDelete = async (id) => {
        if (!(await confirmDialog({ message: "일정을 삭제하시겠습니까?" }))) return;
        return runApiAction({
            request: () => calendarApi.delete(id),
            successMsg: "삭제되었습니다",
            errorMsg: serverMsgOr("삭제 실패"),
            onSuccess: () => { setDialogOpen(false); loadMyEvents(); },
        });
    };

    const sorted = [...events].sort(sortByStartAt);

    return (
        <Box>
            {/* 기간 필터 */}
            <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: "1px solid #e2e8f0", mb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                    <TextField label="시작일" type="date" size="small" value={dateFrom}
                        onChange={e => setDateFrom(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{ width: 160 }} />
                    <Typography sx={{ color: "#94a3b8", fontSize: 14, fontWeight: 600 }}>~</Typography>
                    <TextField label="종료일" type="date" size="small" value={dateTo}
                        onChange={e => setDateTo(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: dateFrom } }}
                        sx={{ width: 160 }} />
                    <ActionButton variant="search" onClick={loadMyEvents} disabled={loading} label="조회" />
                    <Box sx={{ flex: 1 }} />
                    <ActionButton variant="add" onClick={handleAdd} label="일정 추가" />
                </Stack>
            </Paper>

            {/* 일정 개수 */}
            {!loading && (
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                        {dateFrom} ~ {dateTo} 기간 내
                    </Typography>
                    <Chip label={`${sorted.length}건`} size="small"
                        sx={{ bgcolor: "#dbeafe", color: "#1d4ed8", fontWeight: 700, fontSize: 11, borderRadius: 1 }} />
                </Stack>
            )}

            {/* 일정 목록 */}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
                    <CircularProgress sx={{ color: "#2563eb" }} />
                </Box>
            ) : sorted.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 10, color: "#94a3b8" }}>
                    <CalendarMonthRoundedIcon sx={{ fontSize: 48, mb: 1.5, opacity: 0.3 }} />
                    <Typography sx={{ fontSize: 14 }}>해당 기간에 등록한 일정이 없습니다</Typography>
                    <ActionButton variant="secondary" startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                        onClick={handleAdd} sx={{ mt: 2 }} label="일정 추가하기" />
                </Box>
            ) : (
                <Stack spacing={1}>
                    {sorted.map(ev => {
                        const c = getEventColor(ev);   // 유형(카테고리) 색 — 메인 캘린더와 같은 규칙
                        const start = ev.startAt ? toDayjs(ev.startAt) : null;
                        const end = ev.endAt ? toDayjs(ev.endAt) : null;
                        const isMultiDay = start && end && !start.isSame(end, "day");

                        return (
                            <Paper key={ev.id} elevation={0} sx={{
                                p: 2, borderRadius: 1.5, border: "1px solid #e2e8f0",
                                borderLeft: `4px solid ${c.bar}`,
                                transition: "box-shadow 0.15s",
                                "&:hover": { boxShadow: "0 2px 12px rgba(0,0,0,0.07)" },
                            }}>
                                <Stack direction="row" alignItems="flex-start" spacing={2}>
                                    {/* 날짜/시간 컬럼 */}
                                    <Box sx={{
                                        minWidth: 100, textAlign: "center",
                                        bgcolor: "#f8fafc", borderRadius: 1.5, px: 1, py: 0.8,
                                    }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
                                            {start ? formatMonthDayKo(start) : ""}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.3 }}>
                                            {start ? start.format("ddd요일") : ""}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: c.text, fontWeight: 600, mt: 0.5 }}>
                                            {ev.allDay ? "종일" : start ? `${formatTime(start)}${end ? `\n~ ${formatTime(end)}` : ""}` : ""}
                                        </Typography>
                                        {isMultiDay && !ev.allDay && (
                                            <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                                                ~ {end.format("M/D")}
                                            </Typography>
                                        )}
                                        {ev.allDay && isMultiDay && (
                                            <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                                                ~ {formatMonthDayKo(end)}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* 내용 컬럼 */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack direction="row" alignItems="center" spacing={1} mb={0.5} flexWrap="wrap">
                                            <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>
                                                {ev.title}
                                            </Typography>
                                            {ev.deptName && (
                                                <Chip label={ev.deptName} size="small" variant="outlined"
                                                    sx={{ fontSize: 10, borderRadius: 1, height: 20, color: "#2563eb", borderColor: "#93c5fd" }} />
                                            )}
                                        </Stack>
                                        {ev.description && (
                                            <Typography sx={{ fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>
                                                {ev.description.length > 120
                                                    ? ev.description.slice(0, 120) + "…"
                                                    : ev.description}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* 액션 컬럼 */}
                                    <Stack direction="row" spacing={0.5} flexShrink={0} alignItems="center">
                                        <Tooltip title="수정">
                                            <IconButton size="small" onClick={() => handleEdit(ev)}
                                                sx={{ color: "#2563eb", "&:hover": { bgcolor: "#dbeafe" } }}>
                                                <EditRoundedIcon sx={{ fontSize: 17 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="삭제">
                                            <IconButton size="small" onClick={() => handleDelete(ev.id)}
                                                sx={{ color: "#ef4444", "&:hover": { bgcolor: "#fee2e2" } }}>
                                                <DeleteRoundedIcon sx={{ fontSize: 17 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                            </Paper>
                        );
                    })}
                </Stack>
            )}

            <EventDialog
                open={dialogOpen} event={dialogEvent}
                myDeptId={me?.deptId} myDeptName={me?.deptName}
                onSave={handleSave} onDelete={handleDelete}
                onClose={() => setDialogOpen(false)}
                onDone={() => { setDialogOpen(false); loadMyEvents(); }}
            />
        </Box>
    );
}
